import React, { Fragment, ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';

// Constants

import externalEndpoints from '@constants/configs/externalEndpoints';

// Types

import { IsState } from '../../../SsapTypes';
import { phoneNumberTools } from '@utils/tools/phoneNumberTools';

interface IVoterAndOrganDonorInformation {
    exchangeName: string,
    isState: IsState,
    REGISTER_FOR_VOTE: string,
    PHONE: string,
}

const VoterAndOrganDonorInformation = (props: IVoterAndOrganDonorInformation): ReactElement | null => {
    const {
        exchangeName,
        isState,
        PHONE,
        REGISTER_FOR_VOTE
    } = props;

    let resolveVoterRegistrationResource: string, resolveOrganDonorRegistrationResource: string;

    switch (true) {
        case isState.PA:
            resolveVoterRegistrationResource = externalEndpoints.PA_LINKS.VOTER_REGISTRATION;
            resolveOrganDonorRegistrationResource = externalEndpoints.PA_LINKS.ORGAN_DONOR_REGISTRATION;
            break;
        case isState.IL:
            resolveVoterRegistrationResource = externalEndpoints.IL_LINKS.VOTER_REGISTRATION;
            resolveOrganDonorRegistrationResource = externalEndpoints.IL_LINKS.ORGAN_DONOR_REGISTRATION;
            break;
        default:
    }

    const getDonorInformation = () => {
        switch(true) {
            case isState.NJ:
                return (
                    <>
                        <FormattedMessage
                            id = 'ssap.sign_and_review.if_you_are_not_registered'
                            tagName = 'p'
                        />
                        <a
                            className = 'usa-link--external'
                            href = { `https://${ REGISTER_FOR_VOTE }` }
                            target = '_blank'
                            rel = 'noopener noreferrer'
                        >
                            { `https://${ REGISTER_FOR_VOTE }` }
                        </a>
                    </>
                );
            case isState.IL:
            case isState.PA:
                return (
                    <>
                        <FormattedMessage
                            id = 'ssap.sign_and_review.if_you_are_not_registered_organ_donor'
                            tagName = 'p'
                            values = { {
                                exchangeName,
                                register: (
                                    <strong>
                                        <FormattedMessage id='ssap.sign_and_review.register' />
                                    </strong>
                                ),
                            } }
                        />
                        <a
                            className = 'usa-link--external'
                            id='organ_donor_registration_link'
                            href = { resolveOrganDonorRegistrationResource }
                            target = '_blank'
                            rel = 'noopener noreferrer'
                        >
                            { resolveOrganDonorRegistrationResource }
                        </a>
                        <FormattedMessage
                            id = 'ssap.sign_and_review.organ_donor_registration_note'
                            tagName = 'p'
                            values = { {
                                strong: chunks => <strong>{chunks}</strong>,
                                exchangeName,
                                note: (
                                    <strong>
                                        <FormattedMessage id='ssap.sign_and_review.note' />
                                    </strong>
                                ),
                                voluntary: (
                                    <strong>
                                        <FormattedMessage id='ssap.sign_and_review.voluntary' />
                                    </strong>
                                )
                            } }
                        />
                    </>
                );
            default:
                return null;
        }
    };

    const getInformation = (): React.ReactElement | null => {
        switch(true) {
            case isState.NJ:
                return getDonorInformation();
            case isState.PA:
                return getDonorInformation();
            case isState.IL:
                return (
                    <Fragment>
                        <FormattedMessage
                            id = 'ssap.sign_and_review.if_you_are_not_registered'
                            tagName = 'p'
                            values = { {
                                register: (
                                    <strong>
                                        <FormattedMessage id = 'ssap.sign_and_review.register' />
                                    </strong>
                                ),
                                update: (
                                    <strong>
                                        <FormattedMessage id = 'ssap.sign_and_review.update' />
                                    </strong>
                                )
                            } }
                        />
                        <a
                            className = 'usa-link--external'
                            id='voter_registration_link'
                            href = { resolveVoterRegistrationResource }
                            target = '_blank'
                            rel = 'noopener noreferrer'
                        >
                            { resolveVoterRegistrationResource }
                        </a>
                        <FormattedMessage
                            id = 'ssap.sign_and_review.vote_registration_note'
                            tagName = 'p'
                            values = { {
                                exchangeName,
                                note: (
                                    <strong>
                                        <FormattedMessage id='ssap.sign_and_review.note' />
                                    </strong>
                                ),
                                voluntary: (
                                    <strong>
                                        <FormattedMessage id='ssap.sign_and_review.voluntary' />
                                    </strong>
                                ),
                                phone: phoneNumberTools.getAdjustedPhoneNumber(PHONE),
                            } }
                        />
                        { getDonorInformation() }
                    </Fragment>
                );
            default:
                return null;
        }
    };

    return getInformation();
};

export default VoterAndOrganDonorInformation;